import { PNApiOperations } from "@/api/operations"
import { queryClient } from "@/api/constants"
import { AccountOnboardingStateDTO } from "@/api/dto/users"
import { OnboardingState } from "./onboarding-state"
import { Sentry } from "@/telemetry/sentry"
import toast from "react-hot-toast"
import { removeProjectCatalogArtifacts } from "@/lib/catalog/catalog-file-operations"
import { ProjectItem, ProjectService } from "@/store/project/projects-service"
import { ensureSampleProjectMounted } from "@/store/project/sample-project-loader"
import { projectAtom } from "@/store/project/projects-state"
import { store } from "@/store/store"
import { closeSession } from "@/routing/project-session"

// Convert frontend format to backend format
const toBackendFormat = (state: OnboardingState): AccountOnboardingStateDTO => ({
	adaptiveHealingTooltipShown: state.adaptiveHealingTooltipShown,
	adaptiveMaskTooltipShown: state.adaptiveMaskTooltipShown,
	adaptiveCropTooltipShown: state.adaptiveCropTooltipShown,
	adaptivePerspectiveTooltipShown: state.adaptivePerspectiveTooltipShown,
	calibrationTooltipsShown: state.calibrationTooltipsShown,
	colorMatchAppliedToSuggestedShown: state.colorMatchAppliedToSuggestedShown,
	continueReferenceReminderTooltipShown: state.continueReferenceReminderTooltipShown,
	createStyleShown: state.createStyleShown,
	cullingShown: state.cullingShown,
	cullingTooltipsShown: state.cullingTooltipsShown,
	manualCullingTooltipsShown: state.manualCullingTooltipsShown,
	expressSetupTooltipShown: state.expressSetupTooltipShown,
	fullCalibrationTooltipsShown: state.fullCalibrationTooltipsShown,
	hiddenPhotosTooltipShown: state.hiddenPhotosTooltipShown,
	improveStyleTooltipsShown: state.improveStyleTooltipsShown,
	editingTooltipsShown: state.editingTooltipsShown,
	intakeSurveyShown: state.intakeSurveyShown,
	markReferenceReminderTooltipShown: state.markReferenceReminderTooltipShown,
	presetAppliedShown: state.presetAppliedShown,
	presetAppliedToSuggestedShown: state.presetAppliedToSuggestedShown,
	sessionCount: state.sessionCount,
	unsubscribedSurveyShown: state.unsubscribedSurveyShown,
	welcomeCreditsShown: state.welcomeCreditsShown,
	welcomeShown: state.welcomeShown,
})

const isAbortError = (error: unknown): boolean => {
	const errorObject = error as {
		code?: string
		name?: string
		message?: string
		cause?: { name?: string; message?: string }
	}
	const message = errorObject?.message ?? ""
	const code = errorObject?.code ?? ""
	const name = errorObject?.name ?? ""
	const causeName = errorObject?.cause?.name ?? ""
	const combined = `${code} ${name} ${causeName} ${message}`.toLowerCase()
	return (
		combined.includes("abort") ||
		combined.includes("canceled") ||
		combined.includes("cancelled")
	)
}

const getProjects = async (): Promise<ProjectItem[]> => {
	const cached = queryClient.getQueryData<ProjectItem[]>(["projects"])
	if (cached) {
		return cached
	}

	return queryClient.fetchQuery({
		queryKey: ["projects"],
		queryFn: ProjectService.listProjects,
	})
}

const removeSampleProjectFiles = async (handle: FileSystemDirectoryHandle): Promise<void> => {
	// Call the shared file operations directly from the main thread here.
	// Safari is unreliable when FileSystemDirectoryHandle is sent through the
	// catalog worker during onboarding reset, and this flow only needs to clean
	// up known files inside the sample project directory.
	await removeProjectCatalogArtifacts(handle, { ignoreNotFound: true })
}

const resetSampleProjectState = async (): Promise<void> => {
	const projects = await getProjects()
	const sampleProject = projects.find((project) => project.isSampleProject)
	if (!sampleProject) {
		return
	}

	let handle = sampleProject.handle

	const currentProject = store.get(projectAtom)
	if (currentProject?.id === sampleProject.id && currentProject.isSampleProject) {
		await closeSession({ saveChanges: false, reason: "delete_project" })
		handle = currentProject.handle ?? handle
	}

	if (!handle) {
		try {
			handle = await ensureSampleProjectMounted(sampleProject.id)
		} catch (error) {
			console.warn(
				"Failed to mount sample project during onboarding reset, skipping file cleanup:",
				error,
			)
			return
		}
	}

	if (!handle) {
		return
	}

	await removeSampleProjectFiles(handle)
}

// Migrate from localStorage to backend if needed
let migrationAttempted = false

export const migrateOnboardingStateFromLocalStorage = async () => {
	if (migrationAttempted) return
	migrationAttempted = true

	const stored = localStorage.getItem("onboardingState")
	if (!stored) return

	try {
		// Check if backend already has data (not default values)
		const currentUser = queryClient.getQueryData<{
			onboardingState?: AccountOnboardingStateDTO
		}>(["user"])
		if (
			currentUser?.onboardingState?.welcomeShown === true ||
			(currentUser?.onboardingState?.sessionCount ?? 0) > 0
		) {
			console.log("Backend already has onboarding data, skipping migration")
			localStorage.removeItem("onboardingState")
			return
		}

		const storedState: OnboardingState = JSON.parse(stored)

		// Backend will validate the values
		const backendState: AccountOnboardingStateDTO = {
			adaptiveHealingTooltipShown: storedState.adaptiveHealingTooltipShown,
			adaptiveMaskTooltipShown: storedState.adaptiveMaskTooltipShown,
			adaptiveCropTooltipShown: storedState.adaptiveCropTooltipShown,
			adaptivePerspectiveTooltipShown: storedState.adaptivePerspectiveTooltipShown,
			calibrationTooltipsShown: storedState.calibrationTooltipsShown,
			colorMatchAppliedToSuggestedShown: storedState.colorMatchAppliedToSuggestedShown,
			continueReferenceReminderTooltipShown:
				storedState.continueReferenceReminderTooltipShown,
			createStyleShown: storedState.createStyleShown,
			cullingShown: storedState.cullingShown,
			cullingTooltipsShown: storedState.cullingTooltipsShown,
			manualCullingTooltipsShown: storedState.manualCullingTooltipsShown,
			expressSetupTooltipShown: storedState.expressSetupTooltipShown,
			fullCalibrationTooltipsShown: storedState.fullCalibrationTooltipsShown,
			hiddenPhotosTooltipShown: storedState.hiddenPhotosTooltipShown,
			improveStyleTooltipsShown: storedState.improveStyleTooltipsShown,
			editingTooltipsShown: storedState.editingTooltipsShown,
			intakeSurveyShown: storedState.intakeSurveyShown,
			markReferenceReminderTooltipShown: storedState.markReferenceReminderTooltipShown,
			presetAppliedShown: storedState.presetAppliedShown,
			presetAppliedToSuggestedShown: storedState.presetAppliedToSuggestedShown,
			sessionCount: storedState.sessionCount,
			unsubscribedSurveyShown: storedState.unsubscribedSurveyShown,
			welcomeCreditsShown: storedState.welcomeCreditsShown,
			welcomeShown: storedState.welcomeShown,
		}

		await PNApiOperations.updateCurrentUser({ onboardingState: backendState }, "background")
		await queryClient.invalidateQueries({ queryKey: ["user"] })
		localStorage.removeItem("onboardingState")
		console.log("Successfully migrated onboarding state from localStorage to backend")
	} catch (error) {
		console.error("Failed to migrate onboarding state:", error)
		migrationAttempted = false
	}
}

export const updateOnboardingState = async (state: OnboardingState): Promise<void> => {
	const backendState = toBackendFormat(state)
	// Optimistic update
	const currentUser = queryClient.getQueryData<{ onboardingState?: AccountOnboardingStateDTO }>([
		"user",
	])
	if (currentUser) {
		queryClient.setQueryData(["user"], {
			...currentUser,
			onboardingState: backendState,
		})
	}

	await PNApiOperations.updateCurrentUser({ onboardingState: backendState }, "background").catch(
		(error) => {
			if (!isAbortError(error)) {
				Sentry.withScope((scope) => {
					scope.setTag("feature", "onboarding")
					scope.setExtra(
						"route",
						typeof window !== "undefined" ? window.location.pathname : "unknown",
					)
					Sentry.captureException(error)
				})
			}

			console.error("Failed to sync onboarding state:", error)
			void queryClient.invalidateQueries({ queryKey: ["user"] })
		},
	)
}

export const resetOnboarding = async (): Promise<void> => {
	try {
		await resetSampleProjectState()

		await PNApiOperations.updateCurrentUser({ onboardingState: null }, "active")
		await queryClient.invalidateQueries({ queryKey: ["user"] })
		await queryClient.invalidateQueries({ queryKey: ["projects"] })
		toast.success("Onboarding Reset") // i18n-ignore — debug-only dev tool
	} catch (error) {
		console.error("Failed to reset onboarding:", error)
		toast.error("Failed to reset onboarding") // i18n-ignore — debug-only dev tool
		throw error
	}
}
